import { useContext, useMemo, useState } from 'react';
import {
  Card,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  Body1Strong,
  Body1,
  makeStyles,
  Subtitle2,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import {
  EditRegular,
  DismissRegular,
  DeleteRegular,
  MoreHorizontalRegular,
  ArrowUndoRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';

import { ESubmissionStatus, TSubmission, TUpdateSubmissionDialogState } from '../../../types/submissions.types';
import { ConfirmDialog, DateLabel, MenuItemLoading, TConfirmDialogState } from '@shared/components';
import { AvatarGroup, SignInContext } from '@modules/users';
import { USER_GROUPS } from '@modules/users';
import { TFolder, TJurisdictionMap, TSparseItem } from '@modules/items';

import SubmissionStatusBadge from '../../SubmissionStatusBadge';
import { useDeleteSubmission, useTransitionSubmission } from '@modules/submissions/api';
import { useNavigate } from 'react-router-dom';
import UpdateSubmissionDialog from '../dialogs/UpdateSubmissionDialog';
import { useHasFullPermissions } from '../../../helpers/getPermissions.helper';

const useStyles = makeStyles({
  labelRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
    width: '50%',
  },
  jurisdictionLabelRow: {
    width: '100%',
  },
  nextStepContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
  },
  nextStepButton: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    width: 'fit-content',
  },
});

type TProps = {
  folder: TFolder;
  submission: TSubmission;
  jurisdictionItem: TSparseItem | undefined;
};

const SubmissionDetailsCard = ({ folder, submission, jurisdictionItem }: TProps) => {
  const navigate = useNavigate();
  const { user } = useContext(SignInContext);

  const { _id: submissionId, status, dueDate, leads, jurisdiction } = submission;

  // --- Transition Submission Status ---
  const transitionSubmission = useTransitionSubmission();
  const getHandleTransition = (newStatus: ESubmissionStatus) => () =>
    transitionSubmission.mutate({ submissionId, updateData: { newStatus } });
  const isPreparation = status === ESubmissionStatus.IN_PREPARATION;

  // --- Archive Submission ---
  const [confirmDialogState, setConfirmDialogState] = useState<TConfirmDialogState | undefined>();
  const deleteSubmissionMutation = useDeleteSubmission();
  const handleDelete = () => {
    const newDialogState: TConfirmDialogState = {
      isOpen: true,
      title: 'Delete submission?',
      content: 'Are you sure you want to delete this submission?',
      buttonLabel: 'Delete',
      onConfirm: async () => {
        await deleteSubmissionMutation.mutateAsync({ submissionId });
        setConfirmDialogState({ ...newDialogState, isOpen: false });
        navigate(`/product/${submission.product}/submissions`);

        // UX improvement: wait for the dialog to close and pop-up to apper before navigating
      },
    };
    setConfirmDialogState(newDialogState);
  };

  // Create a state object for the update dialog; initially closed.
  const [updateDialogState, setUpdateDialogState] = useState<TUpdateSubmissionDialogState>({
    isOpen: false,
    productName: folder.name,
    submission,
    jurisdictions: folder.jurisdictions,
  });

  // Handler to open the dialog – we update the state object.
  const handleEdit = () => {
    setUpdateDialogState((prev) => ({ ...prev, isOpen: true }));
  };

  let dateLabel: string;
  let dateValue: string;
  const statusHistoryEntry = submission.statusHistory[submission.statusHistory.length - 1];
  if (statusHistoryEntry.status === ESubmissionStatus.DONE) {
    dateLabel = 'Completed on';
    dateValue = statusHistoryEntry.date;
  } else if (statusHistoryEntry.status === ESubmissionStatus.CANCELED) {
    dateLabel = 'Canceled on';
    dateValue = statusHistoryEntry.date;
  } else {
    dateLabel = 'Created on';
    dateValue = submission.createdDate;
  }

  // --- Permissions:
  const hasFullPermissions = useHasFullPermissions(submission);

  /**
   * 1. submission might not be tied to a jurisdction
   * 2. otherwise, jurisdiction might be archived
   */
  const jurisdictionLabel = !jurisdiction
    ? '-'
    : jurisdictionItem?.name || <span className="text-red-500">Archived</span>;

  const classes = useStyles();

  return (
    <>
      <ConfirmDialog state={confirmDialogState} setState={setConfirmDialogState} />

      <Card className="flex flex-col w-[333px]" size="large">
        {/* Card Header */}
        <div className="flex justify-between items-center">
          <Subtitle2>Details</Subtitle2>
          {hasFullPermissions && (
            <Menu positioning="below-end">
              <MenuTrigger disableButtonEnhancement>
                <Button
                  appearance="transparent"
                  icon={<MoreHorizontalRegular />}
                  aria-label="More actions"
                  className="!p-0"
                />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem icon={<EditRegular />} onClick={handleEdit}>
                    Edit details
                  </MenuItem>
                  {isPreparation ? (
                    <>
                      <MenuItemLoading
                        icon={<DismissRegular />}
                        onClick={getHandleTransition(ESubmissionStatus.CANCELED)}
                        loading={transitionSubmission.isPending}
                      >
                        Cancel
                      </MenuItemLoading>
                      <MenuItemLoading
                        icon={<CheckmarkRegular />}
                        onClick={getHandleTransition(ESubmissionStatus.DONE)}
                        loading={transitionSubmission.isPending}
                      >
                        Done
                      </MenuItemLoading>
                    </>
                  ) : (
                    <MenuItemLoading
                      icon={<ArrowUndoRegular />}
                      onClick={getHandleTransition(ESubmissionStatus.IN_PREPARATION)}
                      loading={transitionSubmission.isPending}
                    >
                      Reopen
                    </MenuItemLoading>
                  )}

                  <MenuItem icon={<DeleteRegular />} onClick={handleDelete}>
                    Delete
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
        </div>

        {/* Submission Status */}
        <SubmissionStatusBadge status={status} />

        {/* Leads */}
        <div className={classes.labelRow}>
          <Body1>Submission leads:</Body1>
          <AvatarGroup ids={leads} />
        </div>

        <div className={mergeClasses(classes.labelRow, classes.jurisdictionLabelRow)}>
          <Body1>Jurisdiction:</Body1>
          <Body1Strong>{jurisdictionLabel}</Body1Strong>
        </div>

        {/* Jurisdiction & Deadline */}
        <div className="w-full flex">
          <div className={classes.labelRow}>
            <Body1>{dateLabel}:</Body1>
            <Body1Strong>
              <DateLabel>{dateValue}</DateLabel>
            </Body1Strong>
          </div>
          <div className={classes.labelRow}>
            <Body1>Due date:</Body1>
            <Body1Strong>{dueDate ? <DateLabel>{dueDate}</DateLabel> : '-'}</Body1Strong>
          </div>
        </div>
      </Card>

      <UpdateSubmissionDialog state={updateDialogState} setState={setUpdateDialogState} />
    </>
  );
};

export default SubmissionDetailsCard;
